/* ------------------------------------------------------------------
   LinkInBioPage.js  —  dashboard editor for the user's link-in-bio page
   Layout: tabs (Profile / Links / Design) + persistent action bar
   (URL · Copy · Publish · Save) + live phone preview on the right.
   Links tab also has the contact button, the QR code and an icon per
   link (Part E, T32), and "Moving from Linktree?" import (T33).
------------------------------------------------------------------ */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { PRESETS, themeFromPreset, resolveTheme, themeToCss, presetSwatchBg } from '../../bio/bioThemes';
import LatestPosts from '../../bio/LatestPosts';
import EmailCapture from '../../bio/EmailCapture';
import ProfileKitCard from './ProfileKitCard';
import ContactButtonCard from './ContactButtonCard';
import QrCodeCard from './QrCodeCard';
import LinktreeImportCard from './LinktreeImportCard';
import IconPicker from './IconPicker';
import { BioIcon, iconFor } from '../../bio/bioIcons';
import { ContactButton, EMPTY_CONTACT, contactError, previewContact } from '../../bio/bioContact';

const ax = axios.create({ withCredentials: true });

export default function LinkInBioPage() {
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [pageId, setPageId]     = useState(null);
  const [msg, setMsg]           = useState(null);
  const [tab, setTab]           = useState('profile');

  const [username, setUsername] = useState('');
  const [displayName, setName]  = useState('');
  const [bio, setBio]           = useState('');
  const [avatarUrl, setAvatar]  = useState('');
  const [theme, setTheme]       = useState(() => themeFromPreset('light'));
  const [isPublished, setPub]   = useState(false);
  const [showPosts, setShowPosts]   = useState(false);
  const [postsLimit, setPostsLimit] = useState(6);
  const [posts, setPosts]           = useState([]);
  const [collectEmails, setCollectEmails] = useState(false);
  const [emailHeading, setEmailHeading]   = useState('');
  const [emailButton, setEmailButton]     = useState('');
  const [captureListId, setCaptureListId] = useState('');
  const [emailLists, setEmailLists]       = useState([]);
  const [contact, setContact]             = useState(EMPTY_CONTACT);
  const [contactAvailable, setContactAvailable] = useState(true);
  const [savedUsername, setSavedUsername] = useState('');

  const [links, setLinks]       = useState([]);
  const [unameStatus, setUname] = useState(null);
  const [userId, setUserId]     = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [anLoading, setAnLoading] = useState(false);
  const [range, setRange]         = useState(30);
  const debounceRef = useRef(null);
  const fileRef     = useRef(null);

  const loadAnalytics = (days = range) => {
    setAnLoading(true);
    ax.get('/api/bio/analytics', { params: { days } })
      .then(({ data }) => setAnalytics(data))
      .catch(() => setAnalytics(null))
      .finally(() => setAnLoading(false));
  };

  /* ── load user + existing page ── */
  useEffect(() => {
    (async () => {
      try {
        let profileAvatar = '';
        try {
          const me = await ax.get('/api/auth/me');
          if (me.data?.user?.id) setUserId(me.data.user.id);
          profileAvatar = me.data?.user?.avatar_url || '';
        } catch { /* not logged in */ }

        const { data } = await ax.get('/api/bio');
        if (data.page) {
          setPageId(data.page.id);
          setUsername(data.page.username || '');
          setName(data.page.display_name || '');
          setBio(data.page.bio_text || '');
          setAvatar(data.page.avatar_url || profileAvatar || '');
          setTheme(toEditableTheme(data.page.theme));
          setPub(!!data.page.is_published);
          setShowPosts(!!data.page.show_posts);
          setPostsLimit(data.page.posts_limit || 6);
          setLinks(data.links || []);
          setPosts(data.posts || []);
          setCollectEmails(!!data.page.collect_emails);
          setEmailHeading(data.page.email_heading || '');
          setEmailButton(data.page.email_button || '');
          setCaptureListId(data.page.capture_list_id ? String(data.page.capture_list_id) : '');
          setContact({ ...EMPTY_CONTACT, ...(data.page.contact || {}) });
          setSavedUsername(data.page.username || '');
        } else {
          setAvatar(profileAvatar || '');
        }
        setContactAvailable(data.contactAvailable !== false);
        // mailing lists for the "add subscribers to list" picker (non-fatal)
        try { const { data: ld } = await ax.get('/api/email/lists'); setEmailLists(ld.lists || []); } catch { /* none */ }
      } catch (e) {
        console.error('load bio', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ── debounced username availability ── */
  useEffect(() => {
    if (!username) { setUname(null); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await ax.get('/api/bio/check-username', { params: { u: username } });
        setUname(data);
      } catch { setUname(null); }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [username]);

  /* ── (re)load analytics whenever the Analytics tab opens ── */
  useEffect(() => {
    if (tab === 'analytics') loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const flash = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg(null), 3500); };

  /* ── theme update helpers ── */
  const applyPreset = (key) => setTheme(themeFromPreset(key));
  const updateBg     = (patch) => setTheme(t => ({ ...t, preset: 'custom', bg: { ...t.bg, ...patch } }));
  const updateButton = (patch) => setTheme(t => ({ ...t, preset: 'custom', button: { ...t.button, ...patch } }));
  const updateText   = (color) => setTheme(t => ({ ...t, preset: 'custom', text: color }));

  /* ── avatar upload (reuses the existing public /api/avatar route) ── */
  async function onAvatarFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!userId) { flash('Could not detect your account — reload and try again.', false); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('avatar', file);
      fd.append('userId', userId);
      const { data } = await ax.post('/api/avatar', fd);
      setAvatar(data.avatarUrl);
      flash('Photo uploaded');
    } catch (err) {
      flash(err.response?.data?.error || 'Upload failed', false);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  /* ── save page settings ── */
  async function savePage() {
    const badContact = contactAvailable ? contactError(contact) : null;
    if (badContact) { setTab('links'); flash(badContact, false); return; }
    setSaving(true);
    try {
      const { data } = await ax.put('/api/bio', { username, displayName, bio, avatarUrl, theme, isPublished, showPosts, postsLimit, collectEmails, emailHeading, emailButton, captureListId: captureListId || null,
        ...(contactAvailable ? { contact } : {}) });
      setPageId(data.page.id);
      setSavedUsername(data.page.username || username);
      if (data.page.contact) setContact({ ...EMPTY_CONTACT, ...data.page.contact });
      setContactAvailable(data.contactAvailable !== false);
      flash('Saved!');
    } catch (e) {
      flash(e.response?.data?.error || 'Save failed', false);
    } finally {
      setSaving(false);
    }
  }

  /* ── link CRUD ── */
  async function addLink() {
    if (!pageId) { flash('Save your page first (Profile tab).', false); return; }
    try {
      const { data } = await ax.post('/api/bio/links', { title: 'New link', url: 'https://' });
      setLinks(prev => [...prev, data.link]);
    } catch (e) { flash(e.response?.data?.error || 'Could not add link', false); }
  }
  function updateLink(id, patch) { setLinks(prev => prev.map(l => (l.id === id ? { ...l, ...patch } : l))); }
  async function saveLink(id) {
    const link = links.find(l => l.id === id);
    if (!link) return;
    try {
      await ax.put(`/api/bio/links/${id}`, { title: link.title, url: link.url, icon: link.icon || '', isActive: !!link.is_active });
      flash('Link saved');
    } catch (e) { flash(e.response?.data?.error || 'Could not save link', false); }
  }
  /* picking an icon on an empty link also starts its address (wa.me/, tel:, …) */
  function pickIcon(link, key) {
    const icon = iconFor(key);
    const blank = !link.url || link.url === 'https://';
    updateLink(link.id, { icon: key, ...(icon && blank ? { url: icon.prefix } : {}) });
  }
  async function deleteLink(id) {
    try { await ax.delete(`/api/bio/links/${id}`); setLinks(prev => prev.filter(l => l.id !== id)); }
    catch { flash('Could not delete link', false); }
  }
  /* Import from Linktree: the server already saved the links (and photo) */
  function onLinktreeImported({ imported = [], avatarUrl, avatarError, message }) {
    setLinks(prev => [...prev, ...imported]);
    if (avatarUrl) setAvatar(avatarUrl);
    flash([message, avatarError].filter(Boolean).join(' '), !avatarError);
  }
  async function onDragEnd(result) {
    if (!result.destination) return;
    const reordered = Array.from(links);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setLinks(reordered);
    try { await ax.put('/api/bio/links/reorder', { order: reordered.map(l => l.id) }); }
    catch { flash('Could not save order', false); }
  }

  if (loading) return <div style={{ padding: 40, color: '#9ca3af' }}>Loading…</div>;

  const publicUrl = `${window.location.origin}/u/${username || 'your-name'}`;
  const css = themeToCss(theme);

  return (
    <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-start' }}>

      {/* ───────── Left: editor ───────── */}
      <div style={{ flex: '1 1 460px', minWidth: 320, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>Link in Bio</h1>
          <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>One link for all your content. Share it in every profile.</p>
        </div>

        {/* Persistent action bar */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '10px 16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          {pageId ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <a href={publicUrl} target="_blank" rel="noreferrer"
                style={{ fontSize: 13, color: '#2563eb', textDecoration: 'none', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>
                {publicUrl}
              </a>
              <button onClick={() => { navigator.clipboard?.writeText(publicUrl); flash('Link copied'); }} style={ghostBtn}>Copy</button>
            </div>
          ) : (
            <span style={{ fontSize: 13, color: '#9ca3af' }}>Set a username & save to get your link</span>
          )}

          <div style={{ flex: 1 }} />

          <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
            <input type="checkbox" checked={isPublished} onChange={e => setPub(e.target.checked)} />
            <span style={{ fontSize: 13, color: '#111827', fontWeight: 600 }}>Published</span>
          </label>
          <button onClick={savePage} disabled={saving || (unameStatus && !unameStatus.available)}
            style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>

        {msg && (
          <div style={{
            padding: '10px 14px', borderRadius: 8, fontSize: 14, fontWeight: 600,
            background: msg.ok ? '#ecfdf5' : '#fef2f2', color: msg.ok ? '#059669' : '#dc2626',
            border: `1px solid ${msg.ok ? '#a7f3d0' : '#fecaca'}`,
          }}>{msg.text}</div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid #e5e7eb' }}>
          <Tab id="profile" label="Profile" active={tab === 'profile'} onClick={setTab} />
          <Tab id="links"   label="Links"   active={tab === 'links'}   onClick={setTab} />
          <Tab id="design"  label="Design"  active={tab === 'design'}  onClick={setTab} />
          <Tab id="analytics" label="Analytics" active={tab === 'analytics'} onClick={setTab} />
        </div>

        {/* ── Profile tab ── */}
        {tab === 'profile' && (
          <Card title="Profile">
            <Label>Username</Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#6b7280', fontSize: 14 }}>/u/</span>
              <input value={username}
                onChange={e => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                placeholder="your-name" style={inputStyle} />
            </div>
            {username && unameStatus && (
              <div style={{ fontSize: 12, marginTop: 6, color: unameStatus.available ? '#059669' : '#dc2626' }}>
                {unameStatus.available ? '✓ Available' : (unameStatus.reason || 'Taken')}
              </div>
            )}

            <Label style={{ marginTop: 16 }}>Display name</Label>
            <input value={displayName} onChange={e => setName(e.target.value)} placeholder="Alex Mac" style={inputStyle} />

            <Label style={{ marginTop: 16 }}>Bio</Label>
            <textarea value={bio} onChange={e => setBio(e.target.value)} rows={3} maxLength={500}
              placeholder="Tell visitors who you are…" style={{ ...inputStyle, resize: 'vertical' }} />

            <Label style={{ marginTop: 16 }}>Profile photo</Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{
                width: 60, height: 60, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
                background: '#111827', color: '#fff', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 24, fontWeight: 700,
              }}>
                {avatarUrl
                  ? <img src={avatarUrl} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : (displayName || username || '?').charAt(0).toUpperCase()}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={onAvatarFile} />
                <button onClick={() => fileRef.current?.click()} disabled={uploading} style={{ ...ghostBtn, opacity: uploading ? 0.6 : 1 }}>
                  {uploading ? 'Uploading…' : (avatarUrl ? 'Change photo' : 'Upload photo')}
                </button>
                {avatarUrl && <button onClick={() => setAvatar('')} style={{ ...ghostBtn, color: '#dc2626', borderColor: '#fecaca' }}>Remove</button>}
              </div>
            </div>

            <Label style={{ marginTop: 18 }}>Latest posts</Label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={showPosts} onChange={e => setShowPosts(e.target.checked)} />
              <span style={{ fontSize: 14, color: '#111827' }}>Show my latest posts on the page</span>
            </label>
            {showPosts && (
              <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 13, color: '#374151' }}>How many</span>
                <SegBtns value={String(postsLimit)} options={[['3', '3'], ['6', '6'], ['9', '9'], ['12', '12']]} onChange={v => setPostsLimit(Number(v))} />
              </div>
            )}
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>Auto-pulled from posts you publish through Greatly Social.</div>

            <Label style={{ marginTop: 18 }}>Email capture</Label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={collectEmails} onChange={e => setCollectEmails(e.target.checked)} />
              <span style={{ fontSize: 14, color: '#111827' }}>Collect email signups (sent to your CRM)</span>
            </label>
            {collectEmails && (
              <div style={{ marginTop: 10 }}>
                <input value={emailHeading} onChange={e => setEmailHeading(e.target.value)} maxLength={160}
                  placeholder="Heading — e.g. Join my newsletter" style={inputStyle} />
                <input value={emailButton} onChange={e => setEmailButton(e.target.value)} maxLength={60}
                  placeholder="Button text — e.g. Subscribe" style={{ ...inputStyle, marginTop: 8 }} />
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Also add subscribers to a list</div>
                  <select value={captureListId} onChange={e => setCaptureListId(e.target.value)} style={{ ...inputStyle, background: '#fff' }}>
                    <option value="">CRM only (no list)</option>
                    {emailLists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                  {emailLists.length === 0 && (
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>Create a list in Marketing → Email Marketing → Lists to use this.</div>
                  )}
                </div>
              </div>
            )}
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>Signups always go to Marketing → CRM (tagged “link-in-bio”); a list adds them to that list too.</div>
          </Card>
        )}
        {tab === 'profile' && (
          <ProfileKitCard onUseBio={text => { setBio(text.slice(0, 500)); flash('Bio added. Press Save to keep it.'); }} />
        )}

        {/* ── Links tab: contact button + QR code (BioExtras.dc.html) ── */}
        {tab === 'links' && (
          <>
            <LinktreeImportCard hasPage={!!pageId} onImported={onLinktreeImported} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
              <ContactButtonCard contact={contact} onChange={setContact} available={contactAvailable} />
              <QrCodeCard username={pageId ? savedUsername : ''} onError={text => flash(text, false)} />
            </div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              Also new: 20 more link icons (WhatsApp, Venmo, Cash App, Etsy, Calendly, Zillow and more). Click the icon button on any link.
            </div>
          </>
        )}

        {/* ── Links tab ── */}
        {tab === 'links' && (
          <Card title="Links">
            {!pageId && <div style={{ color: '#9ca3af', fontSize: 14 }}>Save your page first (Profile tab) to start adding links.</div>}
            {pageId && (
              <>
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="links">
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {links.map((link, i) => (
                          <Draggable key={link.id} draggableId={String(link.id)} index={i}>
                            {(p) => (
                              <div ref={p.innerRef} {...p.draggableProps}
                                style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, background: '#fff', ...p.draggableProps.style }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span {...p.dragHandleProps} style={{ cursor: 'grab', color: '#9ca3af', fontSize: 18 }}>⋮⋮</span>
                                  <IconPicker value={link.icon || ''} onChange={key => pickIcon(link, key)} />
                                  <input value={link.title} onChange={e => updateLink(link.id, { title: e.target.value })}
                                    placeholder="Title" style={{ ...inputStyle, flex: 1 }} />
                                </div>
                                <input value={link.url} onChange={e => updateLink(link.id, { url: e.target.value })}
                                  placeholder="https://… (or mailto: / tel:)" style={{ ...inputStyle, marginTop: 8 }} />
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                                  <label style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <input type="checkbox" checked={!!link.is_active}
                                      onChange={e => updateLink(link.id, { is_active: e.target.checked ? 1 : 0 })} />
                                    Active
                                  </label>
                                  <span style={{ fontSize: 12, color: '#9ca3af' }}>{link.click_count || 0} clicks</span>
                                  <div style={{ flex: 1 }} />
                                  <button onClick={() => saveLink(link.id)} style={ghostBtn}>Save</button>
                                  <button onClick={() => deleteLink(link.id)} style={{ ...ghostBtn, color: '#dc2626', borderColor: '#fecaca' }}>Delete</button>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
                <button onClick={addLink} style={{ ...primaryBtn, marginTop: 14 }}>+ Add link</button>
              </>
            )}
          </Card>
        )}

        {/* ── Design tab ── */}
        {tab === 'design' && (
          <Card title="Design">
            <Label>Quick-start themes</Label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
              {Object.keys(PRESETS).map(key => (
                <button key={key} onClick={() => applyPreset(key)} title={PRESETS[key].name}
                  style={{
                    width: 40, height: 40, borderRadius: 8, cursor: 'pointer', background: presetSwatchBg(key),
                    border: theme.preset === key ? '3px solid #2563eb' : '1px solid #d1d5db',
                  }} />
              ))}
            </div>

            <Label style={{ marginTop: 18 }}>Background</Label>
            <SegBtns value={theme.bg.type}
              options={[['solid', 'Solid'], ['gradient', 'Gradient'], ['image', 'Image']]}
              onChange={v => updateBg({ type: v })} />
            {theme.bg.type === 'solid' && (
              <ColorRow label="Color" value={theme.bg.color || '#f8fafc'} onChange={c => updateBg({ color: c })} />
            )}
            {theme.bg.type === 'gradient' && (
              <>
                <ColorRow label="From" value={theme.bg.from || '#2563eb'} onChange={c => updateBg({ from: c })} />
                <ColorRow label="To"   value={theme.bg.to   || '#7c3aed'} onChange={c => updateBg({ to: c })} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                  <span style={{ fontSize: 13, color: '#374151', width: 70 }}>Angle</span>
                  <input type="range" min="0" max="360" value={theme.bg.angle ?? 135}
                    onChange={e => updateBg({ angle: Number(e.target.value) })} style={{ flex: 1 }} />
                  <span style={{ fontSize: 12, color: '#6b7280', width: 38, textAlign: 'right' }}>{theme.bg.angle ?? 135}°</span>
                </div>
              </>
            )}
            {theme.bg.type === 'image' && (
              <>
                <input value={theme.bg.imageUrl || ''} onChange={e => updateBg({ imageUrl: e.target.value })}
                  placeholder="https://…/background.jpg" style={{ ...inputStyle, marginTop: 8 }} />
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>Paste a public image URL.</div>
              </>
            )}

            <Label style={{ marginTop: 18 }}>Button shape</Label>
            <SegBtns value={theme.button.shape}
              options={[['rounded', 'Rounded'], ['pill', 'Pill'], ['square', 'Square']]}
              onChange={v => updateButton({ shape: v })} />

            <Label style={{ marginTop: 14 }}>Button style</Label>
            <SegBtns value={theme.button.fill}
              options={[['solid', 'Solid'], ['outline', 'Outline']]}
              onChange={v => updateButton({ fill: v })} />

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!theme.button.shadow} onChange={e => updateButton({ shadow: e.target.checked })} />
              <span style={{ fontSize: 14, color: '#111827' }}>Hard shadow</span>
            </label>

            <ColorRow label="Button"   value={theme.button.color} onChange={c => updateButton({ color: c })} />
            <ColorRow label="Btn text" value={theme.button.text}  onChange={c => updateButton({ text: c })} />
            <ColorRow label="Text"     value={resolveTheme(theme).text} onChange={updateText} />
          </Card>
        )}

        {/* ── Analytics tab ── */}
        {tab === 'analytics' && (
          <Card title="Analytics">
            {!pageId && <div style={{ color: '#9ca3af', fontSize: 14 }}>Create your page first to see analytics.</div>}
            {pageId && anLoading && <div style={{ color: '#9ca3af', fontSize: 14 }}>Loading…</div>}
            {pageId && !anLoading && analytics && analytics.hasPage && (
              <>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <Stat label="Page views"    value={analytics.views.toLocaleString()} />
                  <Stat label="Total clicks"  value={analytics.totalClicks.toLocaleString()} />
                  <Stat label="Click-through" value={`${analytics.ctr}%`} />
                  {analytics.qr && analytics.qr.available && (
                    <Stat label={`QR code visits (${analytics.rangeDays}d)`} value={analytics.qr.views.toLocaleString()} />
                  )}
                </div>

                {/* trend chart + range toggle */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 22 }}>
                  <Label style={{ margin: 0 }}>Over time</Label>
                  <div style={{ display: 'inline-flex', border: '1px solid #d1d5db', borderRadius: 8, overflow: 'hidden' }}>
                    {[7, 30, 90].map((d, i) => (
                      <button key={d} onClick={() => { setRange(d); loadAnalytics(d); }}
                        style={{
                          padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
                          borderLeft: i ? '1px solid #d1d5db' : 'none',
                          background: range === d ? '#2563eb' : '#fff', color: range === d ? '#fff' : '#374151',
                        }}>{d}d</button>
                    ))}
                  </div>
                </div>
                {analytics.series && <TrendChart series={analytics.series} />}

                <div style={{ marginTop: 22 }}>
                  <Label>Clicks by link</Label>
                  {(!analytics.links || analytics.links.length === 0) && (
                    <div style={{ color: '#9ca3af', fontSize: 14 }}>No links yet.</div>
                  )}
                  {analytics.links && analytics.links.length > 0 && (() => {
                    const max = Math.max(1, ...analytics.links.map(l => l.clicks));
                    return analytics.links.map(l => (
                      <div key={l.id} style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                          <span style={{ color: '#111827', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%' }}>
                            {l.title || 'Untitled'}
                          </span>
                          <span style={{ color: '#6b7280', fontWeight: 600 }}>{l.clicks.toLocaleString()}</span>
                        </div>
                        <div style={{ height: 8, borderRadius: 99, background: '#eef2ff', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${Math.round((l.clicks / max) * 100)}%`, background: '#2563eb', borderRadius: 99, transition: 'width 0.3s ease' }} />
                        </div>
                      </div>
                    ));
                  })()}
                </div>

                <button onClick={loadAnalytics} style={{ ...ghostBtn, marginTop: 8 }}>Refresh</button>
              </>
            )}
            {pageId && !anLoading && analytics && !analytics.hasPage && (
              <div style={{ color: '#9ca3af', fontSize: 14 }}>Save your page first to see analytics.</div>
            )}
          </Card>
        )}
      </div>

      {/* ───────── Right: live preview ───────── */}
      <div style={{ flex: '0 0 320px', position: 'sticky', top: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: 10, textAlign: 'center' }}>
          Live preview
        </div>
        <div style={{ width: 300, margin: '0 auto', borderRadius: 32, border: '10px solid #111827', overflow: 'hidden', height: 600, boxShadow: '0 20px 40px rgba(0,0,0,0.18)' }}>
          <div style={{ background: css.pageBackground, height: '100%', overflowY: 'auto', padding: '36px 18px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{
              width: 76, height: 76, borderRadius: '50%', overflow: 'hidden', background: css.avatar.background,
              color: css.avatar.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 30, fontWeight: 700, marginBottom: 12, border: '2px solid rgba(255,255,255,0.25)',
            }}>
              {avatarUrl ? <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (displayName || username || '?').charAt(0).toUpperCase()}
            </div>
            <div style={{ color: css.textColor, fontSize: 17, fontWeight: 700, textAlign: 'center' }}>{displayName || `@${username || 'your-name'}`}</div>
            {bio && <div style={{ color: css.subColor, fontSize: 13, textAlign: 'center', marginTop: 6 }}>{bio}</div>}
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12, marginTop: 22 }}>
              <ContactButton contact={contactAvailable ? previewContact(contact) : null} css={css} preview compact />
              {links.filter(l => l.is_active).map(l => (
                <div key={l.id} style={{ position: 'relative', textAlign: 'center', padding: '12px 34px', ...css.button, fontWeight: 600, fontSize: 14 }}>
                  {iconFor(l.icon) && <BioIcon name={l.icon} size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />}
                  {l.title || 'Untitled'}
                </div>
              ))}
            </div>
            {collectEmails && <EmailCapture heading={emailHeading} buttonText={emailButton} css={css} preview />}
            {showPosts && <LatestPosts posts={posts.slice(0, postsLimit)} textColor={css.textColor} subColor={css.subColor} />}
            <div style={{ marginTop: 28, color: css.subColor, fontSize: 12 }}>⚡ Made with Greatly Social</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* normalize a saved theme into the full editable shape */
function toEditableTheme(saved) {
  const r = resolveTheme(saved);
  return { preset: (saved && saved.preset) || 'custom', bg: r.bg, button: r.button, text: r.text, sub: r.sub };
}

/* ── small presentational helpers ── */
const inputStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db',
  fontSize: 14, color: '#111827', outline: 'none', boxSizing: 'border-box',
};
const primaryBtn = { padding: '10px 18px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const ghostBtn   = { padding: '6px 12px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const Label = ({ children, style }) => (
  <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, ...style }}>{children}</div>
);
const Card = ({ title, children }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
    <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 16 }}>{title}</div>
    {children}
  </div>
);
function shortDate(s) {
  if (!s) return '';
  const [, m, d] = s.split('-');
  return `${parseInt(m, 10)}/${parseInt(d, 10)}`;
}
const LegendDot = ({ color, label }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#6b7280' }}>
    <span style={{ width: 10, height: 10, borderRadius: 3, background: color, display: 'inline-block' }} />
    {label}
  </span>
);
/* Catmull-Rom → cubic-bezier smoothing for a nice curved line */
function smoothPath(pts) {
  if (!pts.length) return '';
  if (pts.length < 3) return 'M ' + pts.map(p => `${p[0]} ${p[1]}`).join(' L ');
  const t = 0.18;
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const c1x = p1[0] + (p2[0] - p0[0]) * t, c1y = p1[1] + (p2[1] - p0[1]) * t;
    const c2x = p2[0] - (p3[0] - p1[0]) * t, c2y = p2[1] - (p3[1] - p1[1]) * t;
    d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2[0]} ${p2[1]}`;
  }
  return d;
}

const TrendChart = ({ series }) => {
  const { days = [], views = [], clicks = [] } = series || {};
  const n = days.length;
  if (!n) return null;

  const W = 340, H = 152, padL = 26, padR = 12, padT = 14, padB = 22;
  const baseY = H - padB;
  const max = Math.max(1, ...views, ...clicks);
  const x = i => padL + (i * (W - padL - padR)) / Math.max(1, n - 1);
  const y = v => padT + (baseY - padT) * (1 - v / max);
  const ptsOf = arr => arr.map((v, i) => [x(i), y(v)]);
  const total = arr => arr.reduce((a, b) => a + b, 0);

  const vLine = smoothPath(ptsOf(views));
  const cLine = smoothPath(ptsOf(clicks));
  const area  = line => `${line} L ${x(n - 1)} ${baseY} L ${x(0)} ${baseY} Z`;

  const gridVals = [...new Set([0, Math.round(max / 2), max])];

  return (
    <div style={{ marginTop: 12, background: '#fcfdfe', border: '1px solid #f1f5f9', borderRadius: 12, padding: '10px 6px 4px' }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        <defs>
          <linearGradient id="bioGradViews" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2563eb" stopOpacity="0.30" />
            <stop offset="100%" stopColor="#2563eb" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="bioGradClicks" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#16a34a" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#16a34a" stopOpacity="0" />
          </linearGradient>
          <filter id="bioLineShadow" x="-10%" y="-30%" width="120%" height="170%">
            <feDropShadow dx="0" dy="2" stdDeviation="2.2" floodColor="#0f172a" floodOpacity="0.16" />
          </filter>
        </defs>

        {/* gridlines */}
        {gridVals.map(v => (
          <g key={v}>
            <line x1={padL} y1={y(v)} x2={W - padR} y2={y(v)} stroke="#eef2f7" strokeWidth="1" strokeDasharray="3 4" />
            <text x="0" y={y(v) + 3} fontSize="8.5" fill="#cbd5e1">{v}</text>
          </g>
        ))}

        {/* area fills */}
        <path d={area(vLine)} fill="url(#bioGradViews)" />
        <path d={area(cLine)} fill="url(#bioGradClicks)" />

        {/* smooth lines with soft shadow */}
        <g filter="url(#bioLineShadow)">
          <path d={cLine} fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d={vLine} fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </g>

        {/* endpoint dots */}
        <circle cx={x(n - 1)} cy={y(clicks[n - 1])} r="3.6" fill="#16a34a" stroke="#fff" strokeWidth="1.6" />
        <circle cx={x(n - 1)} cy={y(views[n - 1])}  r="3.6" fill="#2563eb" stroke="#fff" strokeWidth="1.6" />

        {/* date labels */}
        <text x={padL} y={H - 6} fontSize="9" fill="#94a3b8">{shortDate(days[0])}</text>
        <text x={W - padR} y={H - 6} fontSize="9" fill="#94a3b8" textAnchor="end">{shortDate(days[n - 1])}</text>
      </svg>

      <div style={{ display: 'flex', gap: 18, justifyContent: 'center', marginTop: 2, marginBottom: 6, fontSize: 12 }}>
        <LegendDot color="#2563eb" label={`Views (${total(views)})`} />
        <LegendDot color="#16a34a" label={`Clicks (${total(clicks)})`} />
      </div>
    </div>
  );
};
const Stat = ({ label, value }) => (
  <div style={{ flex: '1 1 120px', minWidth: 110, background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px' }}>
    <div style={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>{value}</div>
    <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 2 }}>{label}</div>
  </div>
);
const Tab = ({ id, label, active, onClick }) => (
  <button onClick={() => onClick(id)}
    style={{
      padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer', background: 'transparent',
      border: 'none', color: active ? '#2563eb' : '#6b7280',
      borderBottom: active ? '2px solid #2563eb' : '2px solid transparent', marginBottom: -1,
    }}>{label}</button>
);
const SegBtns = ({ value, options, onChange }) => (
  <div style={{ display: 'inline-flex', border: '1px solid #d1d5db', borderRadius: 8, overflow: 'hidden' }}>
    {options.map(([val, label], i) => (
      <button key={val} onClick={() => onChange(val)}
        style={{
          padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
          borderLeft: i ? '1px solid #d1d5db' : 'none',
          background: value === val ? '#2563eb' : '#fff', color: value === val ? '#fff' : '#374151',
        }}>{label}</button>
    ))}
  </div>
);
const ColorRow = ({ label, value, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
    <span style={{ fontSize: 13, color: '#374151', width: 70 }}>{label}</span>
    <input type="color" value={value || '#000000'} onChange={e => onChange(e.target.value)}
      style={{ width: 40, height: 30, padding: 0, border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', background: '#fff' }} />
    <span style={{ fontSize: 12, color: '#9ca3af', fontFamily: 'monospace' }}>{value}</span>
  </div>
);
